import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API, useTheme } from '@/App';
import Sidebar from '@/components/Sidebar';
import WhatsAppEmbeddedSignup from '@/components/WhatsAppEmbeddedSignup';
import InboxAnalytics from '@/components/InboxAnalytics';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useNotifications } from '@/hooks/useNotifications';
import { subscribeToPush, registerServiceWorker } from '@/utils/pwa';
import { useTranslation } from '@/hooks/useTranslation';
import { 
  InboxFilters, 
  BulkActionToolbar, 
  ConversationItem, 
  InboxEmptyState,
  InboxStatsBar 
} from '@/components/inbox';
import { 
  MessageSquare, 
  Menu,
  Bell,
  BellOff,
  RefreshCw,
  BarChart3,
  Bot,
  User,
  Settings,
  Keyboard,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONVERSATIONS_URL = `${BACKEND_URL}/api/conversations?limit=500&skip=0`;
const ARCHIVED_URL = `${BACKEND_URL}/api/conversations?filter=archived&limit=500&skip=0`;

// Snapshot in-memory hasil kunjungan terakhir (per user). Inbox tidak boleh
// dilayani dari cache HTTP (harus real-time), tapi snapshot ini dipakai untuk
// langsung menggambar daftar saat halaman dibuka lagi — data segar tetap diambil
// dari network di belakang lalu menimpanya, jadi tidak ada spinner 1-3 detik.
let inboxSnapshot = null; // { userId, conversations }
const getInboxSnapshot = (userId) =>
  inboxSnapshot && userId && inboxSnapshot.userId === userId ? inboxSnapshot.conversations : null;

// ============================================================================
// MAIN INBOX PAGE COMPONENT
// ============================================================================

const InboxPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { token, user } = useAuth();
  
  // ========== STATE ==========
  const [conversations, setConversations] = useState(() => getInboxSnapshot(user?.id) || []);
  const [loading, setLoading] = useState(() => !getInboxSnapshot(user?.id));
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [hasConnectedChannels, setHasConnectedChannels] = useState(false);
  const [infoDismissed, setInfoDismissed] = useState(() => localStorage.getItem('inbox_info_dismissed') === 'true');
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalConversations, setTotalConversations] = useState(0);
  const listContainerRef = useRef(null);
  const ITEMS_PER_LOAD = 30;
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [handlerFilter, setHandlerFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Handler toggle
  const [switchingAllHandler, setSwitchingAllHandler] = useState(false);
  
  // Notifications
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('inbox-notifications-enabled');
    return saved === 'true' && Notification?.permission === 'granted';
  });

  const headers = useMemo(() => 
    token ? { Authorization: `Bearer ${token}` } : {}, 
    [token]
  );

  // ========== HOOKS ==========
  const { isConnected, onMessage } = useWebSocket();
  const { isSupported: notifSupported, permission, requestPermission, notifyNewMessage } = useNotifications();

  // ========== COMPUTED VALUES ==========
  
  // Filter and sort conversations - ALL filtering is client-side, instant
  const filteredConversations = useMemo(() => {
    const filtered = conversations.filter(conv => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          conv.customer_name?.toLowerCase().includes(query) ||
          conv.customer_phone?.includes(query) ||
          conv.last_message_preview?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Archive filter
      if (statusFilter === 'archived') {
        if (!conv.is_archived) return false;
      } else {
        if (conv.is_archived === true) return false;
        
        // Status filter (only for non-archived)
        if (statusFilter !== 'all') {
          if (statusFilter === 'unread' && (conv.unread_count || 0) === 0) return false;
          if (statusFilter === 'attention' && !conv.needs_attention) return false;
          if (statusFilter === 'resolved' && conv.status !== 'resolved' && conv.status !== 'closed') return false;
          if (statusFilter === 'open' && conv.status !== 'open' && conv.status !== 'active') return false;
          if (statusFilter !== 'unread' && statusFilter !== 'resolved' && statusFilter !== 'open' && statusFilter !== 'attention' && conv.status !== statusFilter) return false;
        }
      }
      
      // Channel filter
      if (channelFilter !== 'all' && conv.channel !== channelFilter) return false;
      
      // Handler filter
      if (handlerFilter !== 'all') {
        const handledBy = (conv.handled_by || '').toLowerCase();
        if (handlerFilter === 'bot' && handledBy !== 'bot') return false;
        if (handlerFilter === 'manual' && !['manual', 'agent', 'human'].includes(handledBy)) return false;
      }
      
      // Priority filter
      if (priorityFilter !== 'all') {
        if (priorityFilter === 'normal') {
          if (conv.priority && conv.priority !== 'normal') return false;
        } else {
          if (conv.priority !== priorityFilter) return false;
        }
      }
      
      return true;
    });
    
    // Sort: Pinned first, then by last_message_at (newest first)
    return filtered.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      const timeA = new Date(a.last_message_at || 0).getTime();
      const timeB = new Date(b.last_message_at || 0).getTime();
      return timeB - timeA;
    });
  }, [conversations, searchQuery, statusFilter, channelFilter, handlerFilter, priorityFilter]);

  // Stats - computed from full in-memory dataset, always accurate
  const stats = useMemo(() => {
    const nonArchived = conversations.filter(c => !c.is_archived);
    const archived = conversations.filter(c => c.is_archived);
    const total = conversations.length;
    const unread = nonArchived.filter(c => (c.unread_count || 0) > 0).length;
    const open = nonArchived.filter(c => c.status === 'open' || c.status === 'active').length;
    const resolved = nonArchived.filter(c => c.status === 'resolved' || c.status === 'closed').length;
    const attention = nonArchived.filter(c => c.needs_attention || c.status === 'attention' || c.status === 'urgent' || c.priority === 'urgent' || c.priority === 'high').length;
    
    return [
      { id: 'total', label: 'Total', value: total, color: 'default' },
      { id: 'unread', label: 'Unread', value: unread, color: unread > 0 ? 'danger' : 'default', onClick: () => setStatusFilter(statusFilter === 'unread' ? 'all' : 'unread'), isActive: statusFilter === 'unread' },
      { id: 'open', label: 'Open', value: open, color: 'success', onClick: () => setStatusFilter(statusFilter === 'open' ? 'all' : 'open'), isActive: statusFilter === 'open' },
      { id: 'resolved', label: 'Resolved', value: resolved, color: resolved > 0 ? 'default' : 'default', onClick: () => setStatusFilter(statusFilter === 'resolved' ? 'all' : 'resolved'), isActive: statusFilter === 'resolved' },
      { id: 'attention', label: 'Attention', value: attention, color: attention > 0 ? 'warning' : 'default' },
      { id: 'archived', label: 'Archived', value: archived.length, color: 'default', onClick: () => setStatusFilter(statusFilter === 'archived' ? 'all' : 'archived'), isActive: statusFilter === 'archived' },
    ];
  }, [conversations, statusFilter]);

  const allHandledByBot = conversations.length > 0 && conversations.every(c => c.handled_by === 'bot');
  const selectedCount = selectedIds.size;

  // Aksi baca/belum-baca di toolbar bulk dibuat kontekstual: kalau ada satu saja
  // yang belum dibaca -> tawarkan "Tandai dibaca", kalau semuanya sudah dibaca ->
  // tawarkan "Tandai belum dibaca".
  const hasUnreadSelected = useMemo(
    () => conversations.some((c) => selectedIds.has(c.id) && (c.unread_count || 0) > 0),
    [conversations, selectedIds]
  );

  // ========== EFFECTS ==========

  // Sync notification state with permission
  useEffect(() => {
    if (permission === 'granted') {
      const saved = localStorage.getItem('inbox-notifications-enabled');
      if (saved === 'true' || saved === null) {
        setNotificationsEnabled(true);
      }
    } else if (permission === 'denied') {
      setNotificationsEnabled(false);
      localStorage.setItem('inbox-notifications-enabled', 'false');
    }
  }, [permission]);

  // Fetch ALL conversations once (including archived) — filtering is client-side
  const fetchArchivedCount = useCallback(() => {}, []);  // No-op, archived count computed from in-memory data

  const fetchConversations = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      
      // Fetch ALL conversations in one call (no filter, high limit)
      const [response, archivedResponse] = await Promise.all([
        axios.get(CONVERSATIONS_URL, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(ARCHIVED_URL, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      if (response.data.success) {
        const allConvs = response.data.conversations || [];
        
        // Archived diambil paralel di atas (bukan berurutan) supaya lebih cepat
        
        let archivedConvs = [];
        if (archivedResponse.data.success) {
          archivedConvs = archivedResponse.data.conversations || [];
        }
        
        // Merge: allConvs (non-archived) + archivedConvs, deduplicate by id
        const mergedMap = new Map();
        allConvs.forEach(c => mergedMap.set(c.id, c));
        archivedConvs.forEach(c => mergedMap.set(c.id, c));
        
        const merged = Array.from(mergedMap.values());
        inboxSnapshot = { userId: user?.id, conversations: merged };
        setConversations(merged);
        setTotalConversations(merged.length);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  const fetchChannels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/channels`, { headers });
      if (response.data.success && response.data.channels?.length > 0) {
        const connected = response.data.channels.some(c => c.status !== 'disconnected');
        setHasConnectedChannels(connected);
      } else {
        setHasConnectedChannels(false);
      }
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    }
  }, [headers]);

  const loadMoreConversations = useCallback(() => {}, []);  // No-op, all data in memory

  // Initial fetch — kalau snapshot ada, daftar sudah tampil: cukup refresh senyap
  useEffect(() => {
    fetchConversations(getInboxSnapshot(user?.id) !== null);
    fetchChannels();
  }, []);

  // No refetch on filter change — all filtering is instant client-side

  // WebSocket updates — real-time new conversations
  useEffect(() => {
    const unsubscribe = onMessage('inbox-page', (data) => {
      if (data.type === 'new_message') {
        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === data.conversation_id);
          if (idx >= 0) {
            // Update existing conversation in place
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              last_message_at: data.timestamp,
              last_message_preview: data.message?.query || data.message?.response,
              unread_count: (updated[idx].unread_count || 0) + 1
            };
            return updated;
          }
          // New conversation — fetch full data and prepend
          axios.get(`${BACKEND_URL}/api/conversations/${data.conversation_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => {
            if (res.data) {
              setConversations(p => [res.data, ...p]);
            }
          }).catch(() => {});
          return prev;
        });

        // Show notification
        if (!document.hasFocus() && data.message?.sender_type === 'customer' && notificationsEnabled) {
          const conv = conversations.find(c => c.id === data.conversation_id);
          notifyNewMessage(conv?.customer_name, data.message?.query, data.conversation_id, navigate);
        }
      } else if (data.type === 'conversation_update') {
        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === data.conversation?.id);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...data.conversation };
            return updated;
          }
          return prev;
        });
      }
    });

    return () => unsubscribe();
  }, [onMessage, conversations, navigate, notifyNewMessage, notificationsEnabled]);

  // Background sync — lightweight polling as safety net (WebSocket handles real-time)
  useEffect(() => {
    if (isConnected) return; // Skip if WebSocket is active
    const interval = setInterval(() => fetchConversations(), 15000); // Every 15s as fallback only
    return () => clearInterval(interval);
  }, [isConnected, fetchConversations]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toLowerCase();
      
      // Navigation
      if (key === 'j' || key === 'k') {
        e.preventDefault();
        // TODO: Implement J/K navigation
      }
      
      // Quick actions
      if (key === 'r' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        fetchConversations(true);
      }
      
      // Escape to cancel selection
      if (key === 'escape' && isSelectionMode) {
        cancelSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSelectionMode]);

  // Auto-subscribe to push if notifications already granted
  useEffect(() => {
    if (notificationsEnabled && permission === 'granted' && user?.id) {
      registerServiceWorker().then(reg => {
        if (reg) subscribeToPush(reg, user.id, token);
      }).catch(() => {});
    }
  }, [user?.id, notificationsEnabled, permission, token]);

  // ========== HANDLERS ==========

  const handleEnableNotifications = async () => {
    if (notificationsEnabled) {
      setNotificationsEnabled(false);
      localStorage.setItem('inbox-notifications-enabled', 'false');
    } else {
      const granted = await requestPermission();
      setNotificationsEnabled(granted);
      localStorage.setItem('inbox-notifications-enabled', granted ? 'true' : 'false');
      // Subscribe to push notifications when user enables
      if (granted && user?.id) {
        try {
          const reg = await registerServiceWorker();
          if (reg) await subscribeToPush(reg, user.id, token);
        } catch (e) {
          console.log('[PUSH] Auto-subscribe skipped:', e);
        }
      }
    }
  };

  const handleSwitchAllHandler = async (toBot) => {
    if (conversations.length === 0) return;
    
    setSwitchingAllHandler(true);
    const targetHandler = toBot ? 'bot' : 'manual';
    
    try {
      const updatePromises = conversations.map(conv => 
        axios.put(`${BACKEND_URL}/api/conversations/${conv.id}/handler`, 
          { handled_by: targetHandler },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      
      await Promise.all(updatePromises);
      setConversations(prev => prev.map(c => ({ ...c, handled_by: targetHandler })));
      toast.success(`Semua percakapan diubah ke mode ${toBot ? 'Bot' : 'Manual'}`);
    } catch (error) {
      console.error('Failed to switch all handlers:', error);
      toast.error('Gagal mengubah handler');
    } finally {
      setSwitchingAllHandler(false);
    }
  };

  // ========== SELECTION HANDLERS ==========

  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
  }, []);

  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      if (newSet.size === 0) {
        setIsSelectionMode(false);
      } else {
        setIsSelectionMode(true);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (selectedIds.size === filteredConversations.length) {
      setSelectedIds(new Set());
      setIsSelectionMode(false);
    } else {
      setSelectedIds(new Set(filteredConversations.map(c => c.id)));
      setIsSelectionMode(true);
    }
  }, [selectedIds.size, filteredConversations]);

  const cancelSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  // ========== BULK ACTIONS ==========

  const performBulkAction = useCallback(async (action) => {
    if (selectedIds.size === 0) return;
    
    setBulkLoading(true);
    try {
      const response = await axios.post(
        `${API}/conversations/bulk/action`,
        {
          conversation_ids: Array.from(selectedIds),
          action: action
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const messages = {
          mark_read: `${response.data.modified_count} percakapan ditandai dibaca`,
          mark_unread: `${response.data.modified_count} percakapan ditandai belum dibaca`,
          archive: `${response.data.modified_count} percakapan diarsipkan`,
          delete: `${response.data.modified_count} percakapan dihapus`
        };
        toast.success(messages[action] || response.data.message);
        cancelSelection();
        fetchConversations(true);
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      toast.error('Gagal melakukan aksi');
    } finally {
      setBulkLoading(false);
    }
  }, [selectedIds, token, cancelSelection, fetchConversations]);

  // ========== QUICK ACTIONS ==========

  const handleQuickPin = useCallback(async (id) => {
    const conv = conversations.find(c => c.id === id);
    const newPinState = !conv?.is_pinned;
    
    try {
      await axios.put(`${BACKEND_URL}/api/conversations/${id}/pin`, {
        is_pinned: newPinState
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setConversations(prev => prev.map(c => 
        c.id === id ? { ...c, is_pinned: newPinState } : c
      ));
      toast.success(newPinState ? 'Percakapan di-pin' : 'Pin dihapus');
    } catch (error) {
      console.error('Pin error:', error);
      toast.error('Gagal mengubah pin');
    }
  }, [conversations, token]);

  const handleQuickArchive = useCallback(async (id) => {
    const conv = conversations.find(c => c.id === id);
    const newArchiveState = !conv?.is_archived;
    const action = newArchiveState ? 'archive' : 'unarchive';
    
    try {
      await axios.post(`${BACKEND_URL}/api/conversations/bulk/action`, {
        conversation_ids: [id],
        action: action
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setConversations(prev => prev.map(c => 
        c.id === id ? { ...c, is_archived: newArchiveState } : c
      ));
      toast.success(newArchiveState ? 'Percakapan diarsipkan' : 'Percakapan dikembalikan');
    } catch (error) {
      console.error('Archive error:', error);
      toast.error('Gagal mengarsipkan');
    }
  }, [conversations, token]);

  // Label "Attention" dari AI bisa dihapus manual kalau agent sudah menangani.
  // (Otomatis hilang juga saat agent mengirim balasan manual — lihat backend.)
  const handleClearAttention = useCallback(async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/conversations/${id}/attention`, {
        needs_attention: false
      }, { headers: { Authorization: `Bearer ${token}` } });

      setConversations(prev => prev.map(c =>
        c.id === id ? { ...c, needs_attention: false } : c
      ));
      toast.success('Label Attention dihapus');
    } catch (error) {
      console.error('Clear attention error:', error);
      toast.error('Gagal menghapus label Attention');
    }
  }, [token]);

  const handleSetPriority = useCallback(async (id, priority) => {    try {
      await axios.put(`${BACKEND_URL}/api/conversations/${id}/priority`, {
        priority: priority
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setConversations(prev => prev.map(c => 
        c.id === id ? { ...c, priority: priority } : c
      ));
      
      const priorityLabels = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' };
      toast.success(`Priority diubah ke ${priorityLabels[priority]}`);
    } catch (error) {
      console.error('Priority error:', error);
      toast.error('Gagal mengubah priority');
    }
  }, [token]);

  const handleQuickStatusChange = useCallback(async (id, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/conversations/${id}/status?status=${newStatus}`, {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConversations(prev => prev.map(c => 
        c.id === id ? { ...c, status: newStatus } : c
      ));
      
      toast.success(newStatus === 'resolved' ? 'Percakapan ditandai selesai' : 'Percakapan dibuka kembali');
    } catch (error) {
      console.error('Status change error:', error);
      toast.error('Gagal mengubah status');
    }
  }, [token]);

  // ========== RENDER ==========

  return (
    <div className={`h-[100dvh] overflow-hidden bg-slate-100 dark:bg-[#0A0A0A] ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="flex h-full">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        <div className="flex-1 lg:ml-64 flex flex-col h-full min-w-0">
          {/* ================================================================ */}
          {/* HEADER - Lower z-index on mobile when sidebar is open */}
          {/* ================================================================ */}
          <header className={`shrink-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 ${sidebarOpen ? 'z-20' : 'z-30'} lg:z-30`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
              {/* Single compact header row: Menu + Title + Actions */}
              <div className="flex items-center justify-between">
                {/* Left: Menu + Title */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setSidebarOpen(true)}
                    className="lg:hidden h-8 w-8"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                  
                  <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    {t('inbox.title')}
                    {stats[1].value > 0 && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                        {stats[1].value}
                      </Badge>
                    )}
                  </h1>
                </div>

                {/* Right: Bot toggle + action buttons */}
                <div className="flex items-center gap-1.5">
                  {/* Bot/Manual Toggle — desktop only, mobile moves to toolbar */}
                  {conversations.length > 0 && (
                    <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 dark:bg-zinc-800 rounded-lg">
                      <User className={`w-3.5 h-3.5 ${!allHandledByBot ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
                      <Switch
                        checked={allHandledByBot}
                        onCheckedChange={handleSwitchAllHandler}
                        disabled={switchingAllHandler}
                        className="data-[state=checked]:bg-indigo-600 scale-90"
                      />
                      <Bot className={`w-3.5 h-3.5 ${allHandledByBot ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
                      {switchingAllHandler && <RefreshCw className="w-3 h-3 animate-spin" />}
                    </div>
                  )}

                  <Button variant="outline" size="icon" onClick={() => fetchConversations(true)} disabled={refreshing} className="h-8 w-8">
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                  </Button>

                  {notifSupported && (
                    <Button variant="outline" size="icon" onClick={handleEnableNotifications} className={`h-8 w-8 ${notificationsEnabled ? 'border-blue-500' : ''}`}>
                      {notificationsEnabled ? <Bell className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> : <BellOff className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />}
                    </Button>
                  )}

                  <Button variant="outline" size="icon" onClick={() => setShowAnalytics(true)} className="h-8 w-8">
                    <BarChart3 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {/* ================================================================ */}
          {/* MAIN CONTENT */}
          {/* ================================================================ */}
          <main className="flex-1 min-h-0 w-full max-w-7xl mx-auto px-3 sm:px-6 py-2 flex flex-col gap-2">
            <WhatsAppEmbeddedSignup variant="banner" />
            
            {/* Search + Filter (1 compact row) */}
            <InboxFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              channelFilter={channelFilter}
              onChannelChange={setChannelFilter}
              handlerFilter={handlerFilter}
              onHandlerChange={setHandlerFilter}
              priorityFilter={priorityFilter}
              onPriorityChange={setPriorityFilter}
              totalCount={totalConversations}
              unreadCount={stats[1].value}
              botToggle={{
                show: conversations.length > 0,
                checked: allHandledByBot,
                onCheckedChange: handleSwitchAllHandler,
                disabled: switchingAllHandler,
                loading: switchingAllHandler
              }}
            />

            {/* Compact Stats Bar */}
            {/* Stats bar: hidden on mobile, shown on desktop */}
            {/* <InboxStatsBar stats={stats} className="sm:hidden" /> */}
            <div className="hidden sm:block">
              <InboxStatsBar stats={stats} />
            </div>

            {/* Bulk Action Toolbar */}
            <BulkActionToolbar
              selectedCount={selectedCount}
              onCancel={cancelSelection}
              onMarkRead={() => performBulkAction('mark_read')}
              onMarkUnread={() => performBulkAction('mark_unread')}
              onArchive={() => performBulkAction('archive')}
              onDelete={() => performBulkAction('delete')}
              isLoading={bulkLoading}
            />

            {/* Conversation List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <InboxEmptyState
                type={conversations.length === 0 ? 'no-conversations' : 'no-results'}
                hasConnectedChannels={hasConnectedChannels}
                searchQuery={searchQuery}
                onConnectChannel={() => navigate('/dashboard')}
                onClearFilters={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setChannelFilter('all');
                  setHandlerFilter('all');
                  setPriorityFilter('all');
                }}
              />
            ) : (
              <>
              {/* Compact info banner - dismissible */}
              {!infoDismissed && (
                <div className="flex items-center gap-2 mx-1 mb-1.5 px-2.5 py-1.5 rounded-md bg-indigo-50/60 dark:bg-blue-900/10 border border-blue-100 dark:border-indigo-800/50">
                  <p className="flex-1 text-[10px] sm:text-xs text-indigo-600/80 dark:text-indigo-400/80">
                    Hanya pesan via Nerava yang tampil. Pesan langsung dari WA/IG/Messenger tidak tercatat.
                  </p>
                  <button 
                    onClick={() => { setInfoDismissed(true); localStorage.setItem('inbox_info_dismissed', 'true'); }}
                    className="flex-shrink-0 text-blue-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-0.5 dark:text-indigo-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div 
                ref={listContainerRef}
                className="space-y-1.5 px-1 pb-2 flex-1 min-h-0 overflow-y-auto overscroll-contain no-scrollbar"
                data-testid="conversation-list-container"
              >
                {/* Select All */}
                {filteredConversations.length > 0 && (
                  <div className="flex items-center justify-between px-2 py-1 sticky top-0 bg-slate-100 dark:bg-[#0A0A0A] z-10">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedIds.size === filteredConversations.length && filteredConversations.length > 0}
                        onCheckedChange={selectAll}
                        className="data-[state=checked]:bg-indigo-600"
                      />
                      <span className="text-xs text-slate-500 dark:text-zinc-400">
                        {selectedIds.size === filteredConversations.length 
                          ? `Batalkan semua (${filteredConversations.length})`
                          : `Pilih semua (${filteredConversations.length})`}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 dark:text-zinc-400">
                      {filteredConversations.length} dari {totalConversations} percakapan
                    </span>
                  </div>
                )}

                {/* Conversation Items */}
                {filteredConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={selectedIds.has(conversation.id)}
                    isSelectionMode={isSelectionMode}
                    onSelect={toggleSelection}
                    onEnterSelectionMode={enterSelectionMode}
                    onClick={() => {
                      if (isSelectionMode) {
                        toggleSelection(conversation.id);
                      } else {
                        navigate(`/inbox/${conversation.id}`);
                      }
                    }}
                    onQuickPin={handleQuickPin}
                    onQuickArchive={handleQuickArchive}
                    onSetPriority={handleSetPriority}
                    onQuickStatusChange={handleQuickStatusChange}
                    onClearAttention={handleClearAttention}
                  />
                ))}
                
                {/* Load More Indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin dark:text-indigo-400" />
                    <span className="ml-2 text-sm text-slate-500 dark:text-zinc-400">Memuat lebih banyak...</span>
                  </div>
                )}
                
                {/* End of List */}
                {!hasMore && conversations.length > ITEMS_PER_LOAD && (
                  <div className="text-center py-4 text-xs text-slate-500 dark:text-zinc-400">
                    Semua {totalConversations} percakapan telah dimuat
                  </div>
                )}
                
                {/* Load More Button (fallback) */}
                {hasMore && !loadingMore && conversations.length >= ITEMS_PER_LOAD && (
                  <div className="flex justify-center py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMoreConversations}
                      data-testid="load-more-btn"
                    >
                      Muat Lebih Banyak
                    </Button>
                  </div>
                )}
              </div>
              </>
            )}

            {/* Tips */}
            <div className="hidden lg:flex items-center justify-center gap-4 text-xs text-slate-500 pt-4 dark:text-zinc-400">
              <span className="flex items-center gap-1">
                <Keyboard className="w-3 h-3" />
                Tips:
              </span>
              <span>Klik kanan untuk opsi Pin, Archive, Priority</span>
              <span><kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-zinc-800 rounded text-[10px]">ESC</kbd> Batalkan</span>
            </div>
          </main>
        </div>
      </div>

      {/* Analytics Modal */}
      <InboxAnalytics 
        isOpen={showAnalytics} 
        onClose={() => setShowAnalytics(false)} 
      />
    </div>
  );
};

export default InboxPage;
